// apps/accounts/src/pages/materials/MaterialRequestDetail.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft,
  RefreshCw,
  CheckCircle,
  XCircle,
  Package,
  User,
  Building2,
  Calendar,
  Edit,
  Trash2,
  TrendingDown,
} from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try { return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; }
};

const statusConfig = {
  Draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700' },
  Pending: { label: 'Pending Approval', color: 'bg-amber-100 text-amber-700' },
  Approved: { label: 'Approved', color: 'bg-blue-100 text-blue-700' },
  Issued: { label: 'Issued', color: 'bg-green-100 text-green-700' },
  Cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700' },
};

const MaterialRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, role } = useAuth();

  // ─── State ──────────────────────────────────────────────────
  const [request, setRequest] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // ─── Role‑based permissions (after state declarations) ────
  const isAdmin = useMemo(() => ['admin'].includes(role), [role]);
  const canApprove = useMemo(() => ['admin', 'site_supervisor'].includes(role), [role]);
  const canIssue = useMemo(() => ['admin', 'store_keeper'].includes(role), [role]);
  const canDelete = useMemo(() => ['admin'].includes(role), [role]);
  // canEdit depends on request and role
  const canEdit = useMemo(() => {
    if (!request) return false;
    return (['admin', 'site_engineer'].includes(role) &&
      !['Approved', 'Issued', 'Cancelled'].includes(request.status));
  }, [request, role]);

  // ─── Data fetching ──────────────────────────────────────────
  const fetchData = async () => {
    try {
      const req = await pb.collection('material_requests').getOne(id, {
        expand: 'project,requested_by,approved_by',
        requestKey: null,
      });
      setRequest(req);

      const itemsData = await pb.collection('material_request_items').getFullList({
        filter: `request = "${id}"`,
        expand: 'material,uom',
        requestKey: null,
      });
      setItems(itemsData);
    } catch (err) {
      if (err.isAbort) return;
      toast.error('Failed to load request');
      navigate('/materials/requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  // ─── Handlers ──────────────────────────────────────────────
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Refreshed');
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this request?')) return;
    try {
      for (const item of items) {
        await pb.collection('material_request_items').delete(item.id);
      }
      await pb.collection('material_requests').delete(id);
      toast.success('Request deleted');
      navigate('/materials/requests');
    } catch (err) {
      toast.error('Delete failed: ' + err.message);
    }
  };

  const handleSubmit = async () => {
    if (request.status !== 'Draft') {
      toast.error('Only draft requests can be submitted');
      return;
    }
    setActionLoading(true);
    try {
      await pb.collection('material_requests').update(id, { status: 'Pending' });
      toast.success('Request submitted for approval');
      fetchData();
    } catch (err) {
      toast.error('Submit failed: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    if (request.status !== 'Pending') {
      toast.error('Only pending requests can be approved');
      return;
    }
    setActionLoading(true);
    try {
      await pb.collection('material_requests').update(id, {
        status: 'Approved',
        approved_by: user.id,
        approved_date: new Date().toISOString(),
      });
      toast.success('Request approved');
      fetchData();
    } catch (err) {
      toast.error('Approval failed: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleIssue = async () => {
    if (request.status !== 'Approved') {
      toast.error('Only approved requests can be issued');
      return;
    }
    setActionLoading(true);
    try {
      for (const item of items) {
        const qtyToIssue = item.quantity_issued || item.quantity_requested;
        if (qtyToIssue <= 0) continue;

        const material = await pb.collection('materials').getOne(item.material);
        const newStock = Math.max(0, (material.current_stock || 0) - qtyToIssue);
        const status = newStock <= 0 ? 'Out of Stock' : (newStock < material.min_stock ? 'Low Stock' : 'Available');
        await pb.collection('materials').update(item.material, {
          current_stock: newStock,
          status,
        });

        await pb.collection('material_transactions').create({
          material: item.material,
          type: 'issue',
          quantity: qtyToIssue,
          date: new Date().toISOString().split('T')[0],
          note: `Issued from request ${request.id}`,
          project: request.project,
          user: user.id,
        });

        await pb.collection('material_request_items').update(item.id, {
          quantity_issued: qtyToIssue,
        });
      }

      await pb.collection('material_requests').update(id, {
        status: 'Issued',
        issued_date: new Date().toISOString(),
      });
      toast.success('Request issued');
      fetchData();
    } catch (err) {
      toast.error('Issue failed: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel this request?')) return;
    setActionLoading(true);
    try {
      await pb.collection('material_requests').update(id, { status: 'Cancelled' });
      toast.success('Request cancelled');
      fetchData();
    } catch (err) {
      toast.error('Cancel failed: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400" />
      </div>
    );
  }

  if (!request) return <div className="p-6 text-center text-slate-500">Request not found</div>;

  const statusInfo = statusConfig[request.status] || statusConfig.Draft;
  const totalItems = items.reduce((sum, item) => sum + (item.quantity_requested || 0), 0);
  const totalIssued = items.reduce((sum, item) => sum + (item.quantity_issued || 0), 0);

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <Link to="/materials/requests" className="text-sm text-amber-600 hover:underline flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to Requests
          </Link>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 mt-1">
            <Package className="w-6 h-6 text-amber-500" />
            Material Request #{request.id.slice(0, 8)}
          </h1>
          <p className="text-sm text-slate-500">
            Status: <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
            {request.status === 'Pending' && <span className="ml-2 text-xs text-amber-600">⏳ Awaiting approval</span>}
            {request.status === 'Approved' && <span className="ml-2 text-xs text-blue-600">✅ Ready for issue</span>}
            {request.status === 'Issued' && <span className="ml-2 text-xs text-green-600">✅ Completed</span>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing} className="gap-2">
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
          {request.status === 'Draft' && canEdit && (
            <Button variant="outline" className="text-amber-600 border-amber-200 hover:bg-amber-50" onClick={() => navigate(`/materials/requests/edit/${id}`)}>
              <Edit className="w-4 h-4 mr-1" /> Edit
            </Button>
          )}
          {canDelete && (request.status === 'Draft' || request.status === 'Pending') && (
            <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={handleDelete}>
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
          )}
          {request.status === 'Draft' && canEdit && (
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={handleSubmit} disabled={actionLoading}>
              <CheckCircle className="w-4 h-4 mr-1" /> {actionLoading ? 'Submitting...' : 'Submit for Approval'}
            </Button>
          )}
          {request.status === 'Pending' && canApprove && (
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleApprove} disabled={actionLoading}>
              <CheckCircle className="w-4 h-4 mr-1" /> {actionLoading ? 'Approving...' : 'Approve'}
            </Button>
          )}
          {request.status === 'Approved' && canIssue && (
            <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={handleIssue} disabled={actionLoading}>
              <TrendingDown className="w-4 h-4 mr-1" /> {actionLoading ? 'Issuing...' : 'Issue Materials'}
            </Button>
          )}
          {(request.status === 'Draft' || request.status === 'Pending' || request.status === 'Approved') && canEdit && (
            <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={handleCancel} disabled={actionLoading}>
              <XCircle className="w-4 h-4 mr-1" /> Cancel
            </Button>
          )}
        </div>
      </div>

      {/* Request Info */}
      <Card>
        <CardContent className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-xs text-slate-500 font-medium">Project</p>
            <p className="font-semibold flex items-center gap-2">
              <Building2 className="w-4 h-4 text-slate-400" />
              {request.expand?.project?.name || 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500 font-medium">Requested By</p>
            <p className="font-semibold flex items-center gap-2">
              <User className="w-4 h-4 text-slate-400" />
              {request.expand?.requested_by?.name || 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500 font-medium">Request Date</p>
            <p className="font-semibold flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              {formatDate(request.request_date)}
            </p>
          </div>
          {request.approved_by && (
            <div>
              <p className="text-xs text-slate-500 font-medium">Approved By</p>
              <p className="font-semibold flex items-center gap-2">
                <User className="w-4 h-4 text-slate-400" />
                {request.expand?.approved_by?.name || 'N/A'}
              </p>
            </div>
          )}
          {request.approved_date && (
            <div>
              <p className="text-xs text-slate-500 font-medium">Approved Date</p>
              <p className="font-semibold">{formatDate(request.approved_date)}</p>
            </div>
          )}
          {request.issued_date && (
            <div>
              <p className="text-xs text-slate-500 font-medium">Issued Date</p>
              <p className="font-semibold">{formatDate(request.issued_date)}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Items Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Material</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">UOM</th>
                <th className="px-4 py-3 text-right font-semibold text-slate-600">Requested Qty</th>
                <th className="px-4 py-3 text-right font-semibold text-slate-600">Issued Qty</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Note</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {items.length === 0 ? (
                <tr><td colSpan="5" className="px-4 py-8 text-center text-slate-500">No items in this request</td></tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3 font-medium">{item.expand?.material?.name || 'N/A'}</td>
                    <td className="px-4 py-3">{item.expand?.uom?.symbol || '—'}</td>
                    <td className="px-4 py-3 text-right font-mono">{item.quantity_requested}</td>
                    <td className="px-4 py-3 text-right font-mono">{item.quantity_issued || 0}</td>
                    <td className="px-4 py-3">{item.note || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot className="bg-slate-50 border-t">
              <tr>
                <td colSpan="2" className="px-4 py-3 text-right font-bold">Totals</td>
                <td className="px-4 py-3 text-right font-bold">{totalItems}</td>
                <td className="px-4 py-3 text-right font-bold">{totalIssued}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </CardContent>
      </Card>

      {/* Note */}
      {request.note && (
        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
          <p className="text-sm text-slate-600"><span className="font-medium">Note:</span> {request.note}</p>
        </div>
      )}
    </div>
  );
};

export default MaterialRequestDetail;